import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Clock } from 'lucide-react';
export function DeadlineCountdown({ dueDate, variant = 'card' }) {
    const parsed = dueDate ? new Date(dueDate) : null;
    const valid = parsed && !Number.isNaN(parsed.getTime());
    let daysLeft = null;
    if (valid) {
        const ms = parsed.getTime() - Date.now();
        daysLeft = Math.ceil(ms / 86400000);
    }
    const accent = pickAccent(daysLeft);
    const label = formatLabel(parsed, daysLeft);
    const formatted = parsed ? parsed.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    }) : 'No deadline set';
    if (variant === 'chip') {
        return /*#__PURE__*/ _jsxs("div", {
            className: `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${accent.chip}`,
            children: [
                /*#__PURE__*/ _jsx(Clock, {
                    className: "h-3.5 w-3.5 flex-shrink-0",
                    "aria-hidden": "true"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "truncate",
                    children: label
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: `rounded-xl border ${accent.cardBorder} bg-white p-5 shadow-sm`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500",
                children: [
                    /*#__PURE__*/ _jsx(Clock, {
                        className: "h-4 w-4",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "Proposal due"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `mt-2 font-display font-semibold ${accent.cardText} ${label.length > 30 ? 'text-xl' : label.length > 20 ? 'text-2xl' : 'text-3xl'}`,
                children: label
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-1 text-sm text-slate-600",
                children: formatted
            })
        ]
    });
}
function pickAccent(daysLeft) {
    if (daysLeft == null) {
        return {
            chip: 'border-slate-200 bg-slate-50 text-slate-600',
            cardBorder: 'border-slate-200',
            cardText: 'text-slate-700'
        };
    }
    if (daysLeft < 3) {
        return {
            chip: 'border-rose-200 bg-rose-50 text-rose-700',
            cardBorder: 'border-rose-300',
            cardText: 'text-rose-700'
        };
    }
    if (daysLeft < 14) {
        return {
            chip: 'border-amber-200 bg-amber-50 text-amber-800',
            cardBorder: 'border-amber-300',
            cardText: 'text-amber-700'
        };
    }
    return {
        chip: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        cardBorder: 'border-emerald-200',
        cardText: 'text-emerald-700'
    };
}
function formatLabel(parsed, daysLeft) {
    if (!parsed || daysLeft == null) return 'No deadline set';
    if (daysLeft === 0) return 'Due today';
    if (daysLeft === 1) return 'Due tomorrow';
    if (daysLeft === -1) return '1 day overdue';
    const abs = Math.abs(daysLeft);
    const suffix = daysLeft < 0 ? ' overdue' : ' left';
    return formatDuration(abs) + suffix;
}
/**
 * Break a number of days into the largest applicable units:
 * years, months, weeks, days — only show non-zero segments.
 *
 *   400 → "1 year, 1 month, 1 week"
 *    45 → "1 month, 2 weeks"
 *    10 → "1 week, 3 days"
 *     3 → "3 days"
 */ function formatDuration(totalDays) {
    const years = Math.floor(totalDays / 365);
    let remaining = totalDays % 365;
    const months = Math.floor(remaining / 30);
    remaining = remaining % 30;
    const weeks = Math.floor(remaining / 7);
    const days = remaining % 7;
    const parts = [];
    if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
    if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
    if (weeks > 0) parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
    if (days > 0 || parts.length === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
    return parts.join(', ');
}
